import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import "../../styles/AdminDashboard.css";
import logo from "../../logos/kodeit-logo.png";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import axiosConfig from "../../axiosConfig";
import "@fortawesome/fontawesome-free/css/all.min.css";
import {
  FiGrid,
  FiUsers,
  FiDatabase,
  FiBookOpen,
  FiInbox,
  FiCompass,
  FiLogOut,
  FiSearch,
  FiBell,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiPlus,
  FiUserPlus,
  FiSettings,
  FiActivity,
  FiMenu,
  FiX,
} from "react-icons/fi";

const NAV_ITEMS = [
  {
    name: "Manage User",
    icon: FiUsers,
    match: ["/admin/register", "/admin/users", "/admin/manage-access", "/admin/edit-user"],
    subItems: [
      { label: "Users", link: "/admin/users" },
      { label: "Manage Access", link: "/admin/manage-access" },
    ],
  },
  {
    name: "Manage Data",
    icon: FiDatabase,
    match: ["/admin/manage-data"],
    link: "/admin/manage-data",
  },
  {
    name: "Book Management",
    icon: FiBookOpen,
    match: ["/admin/books", "/admin/add-book", "/admin/edit-book"],
    link: "/admin/books",
  },
  {
    name: "Explore Books",
    icon: FiCompass,
    match: ["/admin/explore-books", "/admin/explore-covers"],
    link: "/admin/explore-books",
  },
  {
    name: "Inputs",
    icon: FiInbox,
    match: ["/admin/request-access", "/admin/client-inputs"],
    subItems: [
      { label: "Request Access", link: "/admin/request-access" },
      { label: "Client Inputs", link: "/admin/client-inputs" },
    ],
  },
];

const SEARCH_PAGES = [
  { label: "Dashboard", hint: "Overview", path: "/admin" },
  { label: "Register User", hint: "Manage User", path: "/admin/register" },
  { label: "Users", hint: "Manage User", path: "/admin/users" },
  { label: "Manage Access", hint: "Manage User", path: "/admin/manage-access" },
  { label: "Manage Data", hint: "Library catalogues", path: "/admin/manage-data" },
  { label: "Book Management", hint: "Library deliverables", path: "/admin/books" },
  { label: "Add Book", hint: "Book Management", path: "/admin/add-book" },
  { label: "Request Access", hint: "Inputs", path: "/admin/request-access" },
  { label: "Activity Log", hint: "General", path: "/admin/activity-log" },
  { label: "Client Inputs", hint: "Inputs", path: "/admin/client-inputs" },
  { label: "Explore Books", hint: "Catalogue", path: "/admin/explore-books" },
];

const PAGE_TITLES = {
  "/admin": { title: "Dashboard", subtitle: "Library overview and shortcuts" },
  "/admin/register": { title: "Register User", subtitle: "Create a new account" },
  "/admin/users": { title: "Users", subtitle: "Manage people in the library" },
  "/admin/manage-access": { title: "Manage Access", subtitle: "Assign and control book access" },
  "/admin/manage-data": { title: "Manage Data", subtitle: "Countries, grades, subjects and more" },
  "/admin/books": { title: "Book Management", subtitle: "Manage library deliverables" },
  "/admin/add-book": { title: "Add Book", subtitle: "Create a new book record" },
  "/admin/request-access": { title: "Request Access", subtitle: "Approve or deny pending requests" },
  "/admin/activity-log": { title: "Activity Log", subtitle: "Track admin and user actions" },
  "/admin/client-inputs": { title: "Client Inputs", subtitle: "Reviews and client feedback" },
  "/admin/explore-books": { title: "Explore Books", subtitle: "Browse the full catalogue" },
  "/admin/explore-covers": { title: "Explore Covers", subtitle: "Browse book covers" },
};

function pathMatches(pathname, prefixes) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function getPageMeta(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/admin/edit-user")) {
    return { title: "Edit User", subtitle: "Update user details" };
  }
  if (pathname.startsWith("/admin/edit-book")) {
    return { title: "Edit Book", subtitle: "Update book details" };
  }
  return { title: "Admin", subtitle: "Super admin panel" };
}

const DashboardStatsCard = ({ title, count, icon, className, hint }) => {
  const [displayCount, setDisplayCount] = useState(0);
  const ref = useRef(null);
  const animationDuration = 1500;

  useEffect(() => {
    if (typeof count !== "number" || isNaN(count)) {
      setDisplayCount(0);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            let startTimestamp;
            const animateCount = (timestamp) => {
              if (!startTimestamp) startTimestamp = timestamp;
              const progress = Math.min((timestamp - startTimestamp) / animationDuration, 1);
              setDisplayCount(Math.floor(progress * count));
              if (progress < 1) requestAnimationFrame(animateCount);
            };
            requestAnimationFrame(animateCount);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    const node = ref.current;
    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
    };
  }, [count, animationDuration]);

  return (
    <div className={`sa-stat-card ${className || ""}`} ref={ref}>
      <div className="sa-stat-icon">{icon}</div>
      <div className="sa-stat-meta">
        <span className="sa-stat-hint">{hint}</span>
        <div className="sa-stat-count">{displayCount}</div>
      </div>
    </div>
  );
};

function Dashboard() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notifyRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();

  const isDashboardHome = location.pathname === "/admin";
  const pageMeta = getPageMeta(location.pathname);

  const [totalBooksCount, setTotalBooksCount] = useState(0);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState !== null) setSidebarCollapsed(savedState === "true");

    const handleSidebarToggle = (event) => {
      if (event.detail && typeof event.detail.collapsed === "boolean") {
        setSidebarCollapsed(event.detail.collapsed);
      }
    };
    window.addEventListener("sidebar-toggle", handleSidebarToggle);
    return () => window.removeEventListener("sidebar-toggle", handleSidebarToggle);
  }, []);

  useEffect(() => {
    const next = {};
    NAV_ITEMS.forEach((item) => {
      if (pathMatches(location.pathname, item.match)) next[item.name] = true;
    });
    setOpenMenus((prev) => ({ ...prev, ...next }));
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const loadShared = async () => {
      try {
        const reqRes = await axiosConfig.get("/books/book-control/requests", { headers });
        setPendingRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
      } catch {
        setPendingRequests([]);
      }
    };
    loadShared();

    if (!isDashboardHome) {
      setLoadingStats(false);
      return;
    }

    const fetchDashboardStats = async () => {
      setLoadingStats(true);
      setStatsError(null);
      try {
        const [usersRes, booksRes, reviewsRes] = await Promise.all([
          axiosConfig.get("/user", { headers }),
          axiosConfig.get("/books", { headers }),
          axiosConfig.get("/reviews", { headers }).catch(() => ({ data: [] })),
        ]);
        const fetchedUsers = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || [];
        const fetchedBooks = Array.isArray(booksRes.data) ? booksRes.data : booksRes.data.books || [];
        const fetchedReviews = Array.isArray(reviewsRes.data) ? reviewsRes.data : [];
        setTotalUsersCount(fetchedUsers.length);
        setTotalBooksCount(fetchedBooks.length);
        setReviewsCount(fetchedReviews.length);
      } catch (err) {
        console.error("Failed to fetch dashboard stats:", err);
        setStatsError("Failed to load dashboard statistics.");
        setTotalBooksCount(0);
        setTotalUsersCount(0);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchDashboardStats();
  }, [isDashboardHome]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", newState);
      return newState;
    });
  };

  const toggleMenu = (itemName) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      localStorage.setItem("sidebarCollapsed", "false");
      setOpenMenus((prev) => ({ ...prev, [itemName]: true }));
      return;
    }
    setOpenMenus((prev) => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return SEARCH_PAGES.slice(0, 6);
    return SEARCH_PAGES.filter(
      (p) => p.label.toLowerCase().includes(q) || p.hint.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
      if (notifyRef.current && !notifyRef.current.contains(event.target)) setShowNotifications(false);
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goToSearchResult = (path) => {
    navigate(path);
    setSearchQuery("");
    setSearchOpen(false);
  };

  const initials = user?.name?.charAt(0).toUpperCase() || "A";

  return (
    <div className={`sa-app ${sidebarCollapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-mobile-open" : ""}`}>
      {mobileOpen && <div className="sa-backdrop" onClick={() => setMobileOpen(false)} />}

      <aside className="sa-sidebar">
        <div className="sa-brand">
          <Link to="/admin" className="sa-brand-link" onClick={() => setMobileOpen(false)}>
            <img src={logo} alt="Kodeit" className="sa-brand-logo" />
            {!sidebarCollapsed && (
              <div className="sa-brand-text">
                <span>Share Admin</span>
              </div>
            )}
          </Link>
          <button className="sa-mobile-close" onClick={() => setMobileOpen(false)} type="button" aria-label="Close menu">
            <FiX />
          </button>
        </div>

        <div className="sa-nav-scroll">
          <p className="sa-nav-label">{sidebarCollapsed ? "" : "MENU"}</p>
          <ul className="sa-nav">
            <li>
              <Link
                to="/admin"
                className={`sa-nav-link ${isDashboardHome ? "is-active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                <FiGrid />
                {!sidebarCollapsed && <span>Dashboard</span>}
              </Link>
            </li>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const groupActive = pathMatches(location.pathname, item.match);
              const isOpen = !!openMenus[item.name];

              if (item.link) {
                return (
                  <li key={item.name}>
                    <Link
                      to={item.link}
                      className={`sa-nav-link ${groupActive ? "is-active" : ""}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      <Icon />
                      {!sidebarCollapsed && <span>{item.name}</span>}
                    </Link>
                  </li>
                );
              }

              return (
                <li key={item.name} className={`sa-nav-group ${groupActive ? "is-active-group" : ""}`}>
                  <button type="button" className={`sa-nav-link ${groupActive ? "is-active" : ""}`} onClick={() => toggleMenu(item.name)}>
                    <Icon />
                    {!sidebarCollapsed && (
                      <>
                        <span>{item.name}</span>
                        <FiChevronDown className={`sa-chevron ${isOpen ? "is-open" : ""}`} />
                      </>
                    )}
                  </button>
                  {!sidebarCollapsed && isOpen && (
                    <ul className="sa-subnav">
                      {item.subItems.map((sub) => (
                        <li key={sub.link}>
                          <Link
                            to={sub.link}
                            className={location.pathname === sub.link ? "is-active" : ""}
                            onClick={() => setMobileOpen(false)}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="sa-nav-label">{sidebarCollapsed ? "" : "GENERAL"}</p>
          <ul className="sa-nav">
            <li>
              <Link
                to="/admin/activity-log"
                className={`sa-nav-link ${location.pathname === "/admin/activity-log" ? "is-active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                <FiActivity />
                {!sidebarCollapsed && <span>Activity</span>}
              </Link>
            </li>
            <li>
              <button type="button" className="sa-nav-link sa-logout" onClick={handleLogout}>
                <FiLogOut />
                {!sidebarCollapsed && <span>Logout</span>}
              </button>
            </li>
          </ul>
        </div>

        <button type="button" className="sa-collapse-btn" onClick={toggleSidebar}>
          {sidebarCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
          {!sidebarCollapsed && <span>Collapse sidebar</span>}
        </button>
      </aside>

      <div className="sa-shell">
        <header className="sa-header">
          <button type="button" className="sa-mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <FiMenu />
          </button>

          <div className="sa-search" ref={searchRef}>
            <FiSearch />
            <input
              type="search"
              placeholder="Quick search pages, users, books..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchResults[0]) goToSearchResult(searchResults[0].path);
              }}
            />
            <kbd>/</kbd>
            {searchOpen && (
              <div className="sa-search-dropdown">
                {searchResults.length === 0 ? (
                  <p className="sa-empty">No matching pages</p>
                ) : (
                  searchResults.map((item) => (
                    <button key={item.path} type="button" onClick={() => goToSearchResult(item.path)}>
                      <strong>{item.label}</strong>
                      <span>{item.hint}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="sa-header-actions">
            <div className="sa-icon-wrap" ref={notifyRef}>
              <button
                type="button"
                className="sa-icon-btn"
                onClick={() => setShowNotifications((v) => !v)}
                aria-label="Notifications"
              >
                <FiBell />
                {pendingRequests.length > 0 && <em>{pendingRequests.length}</em>}
              </button>
              {showNotifications && (
                <div className="sa-dropdown sa-notify-dropdown">
                  <div className="sa-dropdown-head">
                    <strong>Notifications</strong>
                    <span>{pendingRequests.length} pending</span>
                  </div>
                  {pendingRequests.length === 0 ? (
                    <p className="sa-empty">No pending access requests</p>
                  ) : (
                    pendingRequests.slice(0, 5).map((req, i) => (
                      <button
                        key={`${req.user_id}-${req.book_id}-${i}`}
                        type="button"
                        className="sa-notify-item"
                        onClick={() => {
                          setShowNotifications(false);
                          navigate("/admin/request-access");
                        }}
                      >
                        <strong>{req.book_title || req.title || "Book request"}</strong>
                        <span>{req.user_name || req.name || req.email || "User"}</span>
                      </button>
                    ))
                  )}
                  <Link to="/admin/request-access" className="sa-dropdown-link" onClick={() => setShowNotifications(false)}>
                    View all requests
                  </Link>
                </div>
              )}
            </div>

            <div className={`sa-user ${showDropdown ? "is-open" : ""}`} onClick={() => setShowDropdown((v) => !v)} ref={dropdownRef}>
              <span className="sa-avatar">{initials}</span>
              <div className="sa-user-copy">
                <strong>{user?.name || "Admin"}</strong>
                <span>{user?.email || "admin@example.com"}</span>
              </div>
              {showDropdown && (
                <div className="sa-dropdown sa-user-dropdown">
                  <div className="sa-user-card">
                    <span className="sa-avatar lg">{initials}</span>
                    <div>
                      <strong>{user?.name || "Admin"}</strong>
                      <p>{user?.email || "admin@example.com"}</p>
                    </div>
                  </div>
                  <Link to="/admin/users" onClick={() => setShowDropdown(false)}>
                    <FiSettings /> Account settings
                  </Link>
                  <button type="button" onClick={handleLogout}>
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="sa-content">
          {isDashboardHome ? (
            <div className="sa-home">
              <div className="sa-page-head">
                <div>
                  <h1>Dashboard</h1>
                  <p>
                    Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Here's what's happening across the
                    shared library.
                  </p>
                </div>
                <div className="sa-page-actions">
                  <Link to="/admin/explore-books" className="sa-btn sa-btn-ghost">
                    Explore Books
                  </Link>
                  <Link to="/admin/books" className="sa-btn sa-btn-primary">
                    <FiPlus /> Manage Books
                  </Link>
                </div>
              </div>

              {loadingStats ? (
                <div className="sa-loading">Loading dashboard statistics...</div>
              ) : statsError ? (
                <div className="sa-error">{statsError}</div>
              ) : (
                <div className="sa-stats">
                  <DashboardStatsCard
                    title="Total Books"
                    count={totalBooksCount}
                    hint="Total Books"
                    icon={<i className="fas fa-book"></i>}
                    className="spine-primary"
                  />
                  <DashboardStatsCard
                    title="Total Users"
                    count={totalUsersCount}
                    hint="Total Users"
                    icon={<i className="fas fa-users"></i>}
                    className="spine-mint"
                  />
                  <DashboardStatsCard
                    title="Pending Requests"
                    count={pendingRequests.length}
                    hint="Pending Requests"
                    icon={<i className="fas fa-clock"></i>}
                    className="spine-peach"
                  />
                  <DashboardStatsCard
                    title="Client Inputs"
                    count={reviewsCount}
                    hint="Client Inputs"
                    icon={<i className="fas fa-comment-dots"></i>}
                    className="spine-lilac"
                  />
                </div>
              )}

              <div className="sa-grid">
                <article className="sa-card">
                  <div className="sa-card-head">
                    <h2>Quick actions</h2>
                    <span>Jump into daily work</span>
                  </div>
                  <div className="sa-actions-list">
                    <Link to="/admin/register">
                      <FiUserPlus />
                      <div>
                        <strong>Register User</strong>
                        <span>Invite a new team member</span>
                      </div>
                    </Link>
                    <Link to="/admin/add-book">
                      <FiPlus />
                      <div>
                        <strong>Add Book</strong>
                        <span>Publish a new deliverable</span>
                      </div>
                    </Link>
                    <Link to="/admin/manage-access">
                      <FiUsers />
                      <div>
                        <strong>Manage Access</strong>
                        <span>Assign books to users</span>
                      </div>
                    </Link>
                    <Link to="/admin/manage-data">
                      <FiDatabase />
                      <div>
                        <strong>Manage Data</strong>
                        <span>Update grades, subjects, types</span>
                      </div>
                    </Link>
                  </div>
                </article>

                <article className="sa-card">
                  <div className="sa-card-head">
                    <h2>Access requests</h2>
                    <Link to="/admin/request-access">View all</Link>
                  </div>
                  {pendingRequests.length === 0 ? (
                    <p className="sa-empty padded">No pending requests right now.</p>
                  ) : (
                    <ul className="sa-list">
                      {pendingRequests.slice(0, 5).map((req, i) => (
                        <li key={`${req.user_id}-${req.book_id}-${i}`}>
                          <div className="sa-list-avatar">{(req.user_name || req.name || "U").charAt(0)}</div>
                          <div>
                            <strong>{req.user_name || req.name || req.email || "User"}</strong>
                            <span>{req.book_title || req.title || "Requested a book"}</span>
                          </div>
                          <em className="sa-pill warn">Pending</em>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>

                <article className="sa-card sa-tip">
                  <h2>Library snapshot</h2>
                  <p>
                    Keep catalogue data current so Explore, Access, and Deliverables stay in sync across every admin page.
                  </p>
                  <div className="sa-tip-meta">
                    <span>{pageMeta.title}</span>
                    <Link to="/admin/explore-books">Browse catalogue</Link>
                  </div>
                </article>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
